"use client";

import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { DeleteDialog } from "@/components/molecules/DeleteDialog";
import {
  FormProps,
  CategoryItem,
  CategoryItemChild,
  NestedItem,
  VariantConfig,
} from "./types";
import { useForm3 } from "./hooks";
import {
  CategoryRow,
  NestedItemRow,
  ItemRow,
  Form3OptionalCategoriesFlatPanel,
  mergeOptionalCategoriesFlatUi,
} from "./components";

function useCategoriesOff(
  reduxState: Record<string, unknown> | null,
  sectionData: unknown
): boolean {
  const off = (v: unknown) => v === false || v === 0;
  return useMemo(() => {
    const section =
      sectionData && typeof sectionData === "object" ? (sectionData as Record<string, unknown>) : null;
    const fromSection = section?.use_categories;
    if (fromSection !== undefined && fromSection !== null) {
      return off(fromSection);
    }
    return off(reduxState?.use_categories);
  }, [reduxState?.use_categories, sectionData]);
}

const Form3 = ({ selectedSection, onSave, pageId, pageComponentId, variantDetails }: FormProps) => {
  const {
    // State
    formState,
    setFormState,
    variantConfig,
    reduxState,

    // Configs
    componentFieldsConfig,
    itemFieldsConfig,

    // Feature flags
    hasNestedItemsSupport,

    // Component handlers
    handleComponentFieldsUpdate,
    handleComponentSave,

    // Category handlers
    handleCategoryClick,
    handleNewCategory,
    handleCategoryDelete,
    toggleCategoryExpand,

    // Nested item handlers
    handleNestedItemClick,
    handleNewNestedItem,
    handleNestedItemDelete,
    toggleNestedExpand,

    // Item handlers
    handleItemClick,
    handleNewItem,
    handleItemDelete,

    // Form handlers
    handleFormFieldsUpdate,
    handleCancelForm,
    handleSave,
  } = useForm3({ selectedSection, onSave, pageId, pageComponentId, variantDetails });

  const categoriesOff = useCategoriesOff(reduxState as Record<string, unknown> | null, selectedSection?.data);
  const supportsOptionalCategories =
    (variantConfig as VariantConfig | null)?.supportsOptionalCategoriesToggle === true;
  const visionariesHybridForm =
    (variantConfig as VariantConfig | null)?.visionariesHybridForm === true;
  const optionalCategoriesFlatLabels = useMemo(
    () => mergeOptionalCategoriesFlatUi(variantConfig as VariantConfig | null),
    [variantConfig]
  );

  // Display field names for item rows: variants whose first table columns are an
  // asset/URL override these so the list shows the title, not the upload URL.
  const titleField =
    (variantConfig as VariantConfig | null)?.itemTitleFieldName ||
    itemFieldsConfig[0]?.fieldName ||
    "label";
  const descField =
    (variantConfig as VariantConfig | null)?.itemDescFieldName ||
    itemFieldsConfig[1]?.fieldName ||
    "url";

  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'category' | 'nested' | 'item';
    data: any;
    category?: CategoryItem;
    semester?: NestedItem;
  } | null>(null);

  // Delete handlers with confirmation
  const handleCategoryDeleteWithConfirm = (category: CategoryItem) => {
    setDeleteTarget({ type: 'category', data: category });
    setIsDeleteDialogOpen(true);
  };

  const handleNestedItemDeleteWithConfirm = (category: CategoryItem, semester: NestedItem) => {
    setDeleteTarget({ type: 'nested', data: semester, category });
    setIsDeleteDialogOpen(true);
  };

  const handleItemDeleteWithConfirm = (category: CategoryItem, item: any, semester?: NestedItem) => {
    setDeleteTarget({ type: 'item', data: item, category, semester });
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    switch (deleteTarget.type) {
      case 'category':
        await handleCategoryDelete(deleteTarget.data);
        break;
      case 'nested':
        await handleNestedItemDelete(deleteTarget.category!, deleteTarget.data);
        break;
      case 'item':
        await handleItemDelete(deleteTarget.category!, deleteTarget.data, deleteTarget.semester);
        break;
    }

    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const getDeleteDialogProps = () => {
    if (!deleteTarget) return { title: '', itemName: '', description: '' };

    switch (deleteTarget.type) {
      case 'category':
        return {
          title: 'Delete Category',
          itemName: deleteTarget.data.name,
          description: 'Are you sure you want to delete this category? This will also delete all items in this category.',
        };
      case 'nested':
        return {
          title: 'Delete Semester',
          itemName: deleteTarget.data.name,
          description: 'Are you sure you want to delete this semester? This will also delete all subjects in this semester.',
        };
      case 'item':
        return {
          title: 'Delete Subject',
          itemName: deleteTarget.data[titleField],
          description: 'Are you sure you want to delete this subject? This action cannot be undone.',
        };
    }
  };

  if (!variantConfig) {
    return null;
  }

  const hideCategoryItems = Boolean((variantConfig as any)?.hideCategoryItems);

  const {
    componentFields,
    componentFieldErrors,
    componentFieldMessages,
    formFields,
    formFieldErrors,
    formFieldMessages,
    categories,
    expandedCategoryIds,
    expandedNestedIds,
    selectedItemType,
    selectedCategoryId,
    selectedNestedId,
    selectedItemId,
    editingItemId,
    currentFieldsConfig,
    parentId,
  } = formState;

  const flatBucket = formState.categories.find(
    (c) => c.id === -1 || c.is_uncategorized_bucket
  );
  const treeCategories = visionariesHybridForm
    ? formState.categories.filter((c) => c.id !== -1 && !c.is_uncategorized_bucket)
    : formState.categories;

  if (supportsOptionalCategories && categoriesOff && !visionariesHybridForm) {
    return (
      <div className="flex flex-col h-full">
        <Form3OptionalCategoriesFlatPanel
          labels={optionalCategoriesFlatLabels}
          variantConfig={variantConfig as VariantConfig | null}
          reduxState={reduxState as Record<string, unknown> | null}
          formState={formState}
          componentFieldsConfig={componentFieldsConfig}
          itemFieldsConfig={itemFieldsConfig}
          handleComponentFieldsUpdate={handleComponentFieldsUpdate}
          handleComponentSave={handleComponentSave}
          handleItemClick={handleItemClick}
          handleNewItem={handleNewItem}
          handleItemDelete={handleItemDelete}
          handleFormFieldsUpdate={handleFormFieldsUpdate}
          handleCancelForm={handleCancelForm}
          handleSave={handleSave}
          pageComponentId={pageComponentId}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Component Settings Section */}
      {componentFieldsConfig.length > 0 && (
        <div className="mb-6 border-b pb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <span>📝</span> Component Settings
          </h3>

          <FieldsFormatter
            data={componentFields}
            fields={componentFieldsConfig}
            updateParent={true}
            onChange={handleComponentFieldsUpdate}
            validationErrors={componentFieldErrors}
            validationMessages={componentFieldMessages}
            className="p-0 bg-transparent"
          />

          <div className="mt-4 flex flex-col gap-3">
            <Button onClick={handleComponentSave} classType="admin">
              {reduxState?.id ? "Update Component" : "Save Component"}
            </Button>
            {!pageComponentId && (
              <p className="text-sm text-red-600">
                Cannot attach FAQ data: this section has no page component id. Try
                re-adding the section or refreshing the editor.
              </p>
            )}
            {pageComponentId && !parentId && (
              <p className="text-xs text-gray-500 leading-relaxed">
                Tip: a parent record is also created automatically when you add a category or
                your first FAQ (using the fields above). <strong>Save Component</strong> is only
                needed if you want to persist these settings first without adding categories.
              </p>
            )}
          </div>
        </div>
      )}

      {visionariesHybridForm && parentId && pageComponentId && (
        <div className="mb-6 flex-1 overflow-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <span>✨</span> Visionaries (pool)
            </h3>
            <button
              type="button"
              onClick={() => flatBucket && handleNewItem(flatBucket)}
              disabled={!flatBucket}
              className="flex items-center gap-1 text-sm px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <Plus className="w-3.5 h-3.5" />
              Add visionary
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 max-h-96 overflow-y-auto">
            {(flatBucket?.items ?? []).length > 0 ? (
              <div className="space-y-2">
                {(flatBucket?.items ?? []).map((item) => {
                  const isItemSelected =
                    selectedItemType === "item" && selectedItemId === item.id;
                  return (
                    <ItemRow
                      key={item.id}
                      item={item}
                      isSelected={isItemSelected}
                      titleField={titleField}
                      descField={descField}
                      onClick={() => flatBucket && handleItemClick(flatBucket, item)}
                      onEdit={() => flatBucket && handleItemClick(flatBucket, item)}
                      onDelete={() =>
                        flatBucket && handleItemDeleteWithConfirm(flatBucket, item)
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No visionaries in the pool yet. Click &quot;Add visionary&quot; to create a card.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories & Items Tree Section */}
      {pageComponentId && (
        <div className="mb-6 flex-1 overflow-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <span>📁</span>{" "}
              {visionariesHybridForm ? "Categories" : "Categories & Items"}
            </h3>
            <button
              onClick={handleNewCategory}
              className="flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              New Category
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 max-h-96 overflow-y-auto">
            {treeCategories.length > 0 ? (
              <div className="space-y-2">
                {treeCategories.map((category) => {
                  const isExpanded = expandedCategoryIds.has(category.id);
                  const isCategorySelected =
                    selectedItemType === "category" && selectedCategoryId === category.id;
                  const hasItems = category.items && category.items.length > 0;
                  const hasSemesters = category.semesters && category.semesters.length > 0;

                  return (
                    <div key={category.id}>
                      {/* Category Row */}
                      <CategoryRow
                        category={category}
                        isExpanded={isExpanded}
                        isSelected={isCategorySelected}
                        hasNestedItemsSupport={hasNestedItemsSupport}
                        hideChildAdd={hideCategoryItems}
                        hideExpandToggle={hideCategoryItems && !hasNestedItemsSupport}
                        onToggleExpand={() => toggleCategoryExpand(category.id)}
                        onClick={() => handleCategoryClick(category)}
                        onEdit={() => handleCategoryClick(category)}
                        onDelete={() => handleCategoryDeleteWithConfirm(category)}
                        onAddItem={() => handleNewItem(category)}
                        onAddNestedItem={() => handleNewNestedItem(category)}
                      />

                      {/* Expanded Content (skip when category row hides expand — no child rows under category) */}
                      {isExpanded &&
                        !(hideCategoryItems && !hasNestedItemsSupport) && (
                        <div className="ml-8 mt-2 space-y-2">
                          {/* Three-level: Semesters -> Items */}
                          {hasSemesters &&
                            category.semesters!.map((semester: NestedItem) => {
                              const isNestedExpanded = expandedNestedIds.has(semester.id);
                              const isNestedSelected =
                                selectedItemType === "nested" && selectedNestedId === semester.id;
                              const semesterHasItems = semester.items && semester.items.length > 0;

                              return (
                                <div key={semester.id}>
                                  <NestedItemRow
                                    nestedItem={semester}
                                    isExpanded={isNestedExpanded}
                                    isSelected={isNestedSelected}
                                    onToggleExpand={() => toggleNestedExpand(semester.id)}
                                    onClick={() => handleNestedItemClick(category, semester)}
                                    onEdit={() => handleNestedItemClick(category, semester)}
                                    onDelete={() => handleNestedItemDeleteWithConfirm(category, semester)}
                                    onAddItem={() => handleNewItem(category, semester)}
                                  />

                                  {/* Items under semester */}
                                  {semesterHasItems && isNestedExpanded && (
                                    <div className="ml-8 mt-2 space-y-2">
                                      {semester.items!.map((item) => {
                                        const isItemSelected =
                                          selectedItemType === "item" && selectedItemId === item.id;

                                        return (
                                          <ItemRow
                                            key={item.id}
                                            item={item}
                                            isSelected={isItemSelected}
                                            titleField={
                                              itemFieldsConfig[0]?.fieldName || "subject_name"
                                            }
                                            descField={itemFieldsConfig[1]?.fieldName || "credits"}
                                            showPriority={false}
                                            onClick={() =>
                                              handleItemClick(category, item, semester)
                                            }
                                            onEdit={() =>
                                              handleItemClick(category, item, semester)
                                            }
                                            onDelete={() =>
                                              handleItemDeleteWithConfirm(category, item, semester)
                                            }
                                          />
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                          {/* Two-level: Direct Items */}
                          {!hasSemesters &&
                            !hideCategoryItems &&
                            hasItems &&
                            category.items!.map((item) => {
                              const isItemSelected =
                                selectedItemType === "item" && selectedItemId === item.id;

                              return (
                                <ItemRow
                                  key={item.id}
                                  item={item}
                                  isSelected={isItemSelected}
                                  titleField={titleField}
                                  descField={descField}
                                  onClick={() => handleItemClick(category, item)}
                                  onEdit={() => handleItemClick(category, item)}
                                  onDelete={() => handleItemDeleteWithConfirm(category, item)}
                                />
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No categories yet. Click "New Category" to add one.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Section */}
      {selectedItemType && pageComponentId && (
        <div className="border-t pt-6">
          <h4 className="font-semibold mb-4">
            {selectedItemType === "category"
              ? editingItemId
                ? "Edit Category"
                : "Add New Category"
              : selectedItemType === "nested"
                ? editingItemId
                  ? "Edit Item"
                  : "Add New Item"
                : editingItemId
                  ? "Edit Item"
                  : "Add New Item"}
          </h4>

          <FieldsFormatter
            data={formFields}
            fields={currentFieldsConfig}
            updateParent={true}
            onChange={handleFormFieldsUpdate}
            validationErrors={formFieldErrors}
            validationMessages={formFieldMessages}
            className="p-0 bg-transparent"
          />

          {selectedItemType === "category" && visionariesHybridForm && (
            <div className="mt-6 space-y-2">
              <p className="text-sm font-medium">Visionaries in this category</p>
              <p className="text-xs text-gray-500">
                When &quot;Default (all)&quot; is on, all pool visionaries are included. Otherwise,
                choose which pool cards appear under this category.
              </p>
              <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white p-3 space-y-2">
                {(flatBucket?.items ?? []).length === 0 ? (
                  <p className="text-sm text-gray-500">Add visionaries to the pool first.</p>
                ) : (
                  (flatBucket?.items ?? []).map((item: CategoryItemChild) => {
                    const pid = Number(item.id);
                    const checked = (formState.categoryMembershipIds ?? []).includes(pid);
                    const disabled =
                      formFields.is_default === true ||
                      formFields.is_default === 1 ||
                      formFields.is_default === "1";
                    const label =
                      (item as { profile_name?: string }).profile_name ||
                      `Visionary #${item.id}`;
                    return (
                      <label
                        key={item.id}
                        className="flex cursor-pointer items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => {
                            setFormState((prev) => {
                              const cur = new Set(prev.categoryMembershipIds ?? []);
                              if (cur.has(pid)) cur.delete(pid);
                              else cur.add(pid);
                              return { ...prev, categoryMembershipIds: [...cur] };
                            });
                          }}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-2">
            <Button onClick={handleSave} classType="admin" className="flex-1">
              {editingItemId ? "Update" : "Save"}
            </Button>
            <Button
              onClick={handleCancelForm}
              classType="admin"
              className="px-4 bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteDialog
          open={isDeleteDialogOpen}
          title={getDeleteDialogProps().title}
          itemName={getDeleteDialogProps().itemName}
          description={getDeleteDialogProps().description}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default Form3;

